import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, LayoutGrid, Sparkles, Heart, Store, X, Search } from 'lucide-react';
import { t } from '../lib/i18n';
import { useAuth } from '../context/AuthContext';
import CategoryGrid from './CategoryGrid';

export const openAssistant = () => window.dispatchEvent(new CustomEvent('dealgo:assistant', { detail: 'toggle' }));
export const openMobileSearch = () => window.dispatchEvent(new CustomEvent('dealgo:search'));

// Bottom sheet used by the mobile tab bar (categories, search).
export const Sheet = ({ open, onClose, title, children, testId }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="md:hidden fixed inset-0 z-[120]" data-testid={testId}>
      <button aria-label={t('Fermer')} onClick={onClose} className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" data-testid={`${testId}-backdrop`} />
      <div className="absolute inset-x-0 bottom-0 max-h-[85vh] flex flex-col rounded-t-3xl bg-[#0b0b0b] border-t border-[#262626] shadow-2xl sheet-in pb-[env(safe-area-inset-bottom)]">
        <div className="mx-auto mt-2.5 h-1.5 w-12 rounded-full bg-white/20" />
        <div className="flex items-center justify-between px-5 pt-3 pb-1">
          <h2 className="text-base font-black text-white">{title}</h2>
          <button onClick={onClose} aria-label={t('Fermer')} data-testid={`${testId}-close`} className="w-9 h-9 rounded-full bg-white/10 text-white flex items-center justify-center"><X size={18} /></button>
        </div>
        <div className="overflow-y-auto px-2 pb-4">{children}</div>
      </div>
    </div>
  );
};

const Tab = ({ to, onClick, icon: Icon, label, active, badge, testId }) => {
  const cls = `relative flex flex-col items-center justify-center gap-0.5 flex-1 h-full text-[10px] font-bold tracking-wide transition-colors ${active ? 'text-[#E50914]' : 'text-[#B3B3B3]'}`;
  const inner = (
    <>
      <span className={`relative flex items-center justify-center w-11 h-7 rounded-full transition-colors ${active ? 'bg-[#E50914]/15' : ''}`}>
        <Icon size={21} strokeWidth={active ? 2.5 : 2} />
        {badge > 0 && <span className="absolute -top-1 -end-1 min-w-[16px] h-4 px-1 rounded-full bg-[#E50914] text-white text-[9px] font-black flex items-center justify-center" data-testid={`${testId}-badge`}>{badge > 9 ? '9+' : badge}</span>}
      </span>
      <span>{label}</span>
    </>
  );
  return to ? <Link to={to} onClick={onClick} className={cls} data-testid={testId} aria-current={active ? 'page' : undefined}>{inner}</Link>
    : <button onClick={onClick} className={cls} data-testid={testId} aria-pressed={active}>{inner}</button>;
};

const MobileTabBar = ({ categories = [], activeCategory, onCategorySelect, categoryCounts, searchQuery = '', onSearchChange }) => {
  const { pathname, state } = useLocation();
  const navigate = useNavigate();
  const { favorites } = useAuth();
  const [sheet, setSheet] = useState(null);
  useEffect(() => { setSheet(null); }, [pathname]);
  useEffect(() => {
    const onSearch = () => setSheet('search');
    window.addEventListener('dealgo:search', onSearch);
    return () => window.removeEventListener('dealgo:search', onSearch);
  }, []);

  const isHome = pathname === '/' && state?.tab !== 'jaime';
  const isLiked = pathname === '/' && state?.tab === 'jaime';
  const isStores = pathname.startsWith('/magasins');

  return (
    <>
      <nav className="mobile-tabbar md:hidden fixed inset-x-0 bottom-0 z-[100] bg-[#0b0b0b]/95 backdrop-blur-md border-t border-[#262626] pb-[env(safe-area-inset-bottom)]" data-testid="mobile-tabbar" aria-label="Principal">
        <div className="flex items-stretch h-16 px-1">
          <Tab to="/" icon={Home} label={t('Accueil')} active={isHome && !sheet} testId="tab-home" onClick={() => navigate('/', { state: { tab: 'tous' } })} />
          <Tab onClick={() => setSheet(sheet === 'categories' ? null : 'categories')} icon={LayoutGrid} label={t('Catégories')} active={sheet === 'categories' || !!activeCategory} testId="tab-categories" />
          <button onClick={openAssistant} data-testid="tab-assistant" aria-label={t('Demander à l’IA')}
            className="relative flex-1 flex flex-col items-center justify-end pb-1.5 text-[10px] font-bold text-white">
            <span className="absolute -top-5 w-14 h-14 rounded-full bg-[#E50914] text-white flex items-center justify-center shadow-[0_8px_24px_rgba(229,9,20,0.5)] border-4 border-[#0b0b0b] active:scale-95 transition-transform">
              <Sparkles size={24} />
            </span>
            <span className="mt-auto truncate max-w-full px-1">{t('Assistant')}</span>
          </button>
          <Tab onClick={() => navigate('/', { state: { tab: 'jaime' } })} icon={Heart} label={t('Favoris')} active={isLiked} badge={favorites.length} testId="tab-liked" />
          <Tab to="/magasins" icon={Store} label={t('Magasins')} active={isStores} testId="tab-stores" />
        </div>
      </nav>

      <Sheet open={sheet === 'categories'} onClose={() => setSheet(null)} title={t('Catégories')} testId="categories-sheet">
        <CategoryGrid categories={categories} activeCategory={activeCategory} counts={categoryCounts} cols={2}
          onSelect={(slug) => { onCategorySelect && onCategorySelect(slug); setSheet(null); }} />
      </Sheet>

      <Sheet open={sheet === 'search'} onClose={() => setSheet(null)} title={t('Rechercher')} testId="search-sheet">
        <div className="px-3 pt-2 space-y-3">
          <div className="relative">
            <Search size={18} className="absolute start-4 top-1/2 -translate-y-1/2 text-[#808080]" />
            <input autoFocus type="search" value={searchQuery} onChange={(e) => onSearchChange?.(e.target.value)} placeholder={t('Rechercher un produit ou un magasin')} data-testid="mobile-search-input"
              className="w-full h-12 ps-11 pe-4 rounded-full bg-[#1a1a1a] border border-[#262626] text-white text-sm placeholder:text-[#808080] focus:outline-none focus:border-[#E50914]" />
          </div>
          <button onClick={() => setSheet(null)} data-testid="mobile-search-submit" className="w-full h-11 rounded-full bg-[#E50914] text-white text-sm font-bold">{t('Voir les résultats')}</button>
        </div>
      </Sheet>
    </>
  );
};

export default MobileTabBar;
